import React, { useRef } from 'react';
import { type ForgeDoc } from '@atlassian/forge-ui-types';
import { Iframe } from '../iframe';
import { useCallbackOne } from 'use-memo-one';
import { WorkerWebRuntime } from '../worker-runtime';
import { fg } from '@atlaskit/platform-feature-flags';

interface WebRuntimeProps {
	setError?: (error: Error) => void;
	setForgeDoc: (forgeDoc: ForgeDoc) => void;
}

type IframeProps = Parameters<typeof Iframe>[0];

export const WebRuntime = ({
	setForgeDoc,
	setError,
	accountId,
	apolloClient,
	contextIds,
	extension,
	components,
	coreData,
	extensionData,
	extensionPayload,
	extensionViewData,
	getContextToken,
	timezone,
	locale,
	bridge,
	...restProps
}: WebRuntimeProps & IframeProps): React.JSX.Element => {
	const forgeReactMajorVersion = useRef<number | null>(null);

	const combinedBridge = {
		onError: ({ error }: { error: Error }) => {
			if (setError) {
				setError(error);
			}
		},
		...bridge,
		reconcile: ({ forgeDoc }: { forgeDoc: ForgeDoc }) => {
			Object.freeze(forgeDoc);
			setForgeDoc(forgeDoc);
			if (forgeReactMajorVersion.current === null && forgeDoc.forgeReactMajorVersion) {
				forgeReactMajorVersion.current = forgeDoc.forgeReactMajorVersion;
			}
		},
	};

	const getCurrentForgeReactMajorVersion = useCallbackOne(() => {
		return forgeReactMajorVersion.current;
	}, [forgeReactMajorVersion]);

	// this is essential based on feature gate for now.
	// In the future, this check should inspect extension / manifest prop to
	// determine whether to use the worker runtime or not.
	if (fg('forge-ui-worker-runtime')) {
		return (
			<WorkerWebRuntime
				accountId={accountId}
				apolloClient={apolloClient}
				contextIds={contextIds}
				extension={extension}
				coreData={coreData}
				components={components}
				extensionData={extensionData}
				extensionPayload={extensionPayload}
				extensionViewData={extensionViewData}
				getContextToken={getContextToken}
				getCurrentForgeReactMajorVersion={getCurrentForgeReactMajorVersion}
				timezone={timezone}
				locale={locale}
				bridge={combinedBridge}
				appId={extension.appId!}
				{...restProps}
				onError={(err) => setError && setError(new Error(err))}
			/>
		);
	}

	return (
		<Iframe
			accountId={accountId}
			apolloClient={apolloClient}
			contextIds={contextIds}
			extension={extension}
			coreData={coreData}
			components={components}
			extensionData={extensionData}
			extensionPayload={extensionPayload}
			extensionViewData={extensionViewData}
			getContextToken={getContextToken}
			timezone={timezone}
			locale={locale}
			bridge={combinedBridge}
			isHidden={true}
			getCurrentForgeReactMajorVersion={getCurrentForgeReactMajorVersion}
			{...restProps}
			onError={(err) => setError && setError(new Error(err))}
		/>
	);
};
